import { computed, inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { ToastService } from '@/app/shared/ui/notifications/toast.service';
import {
  StaffAltaProcessResult,
  StaffAltaValidationResult,
  StaffAltaWizardStep,
} from '../models/staff-alta.model';
import { StaffType } from '../models/staff.model';
import { StaffAltaProcessRepository } from '../services/staff-alta-process.repository';
import { StaffAltaValidationRepository } from '../services/staff-alta-validation.repository';
import { perType, StaffTypeSelection } from './staff-type.selection';
import { notifyStaffBatchLimitExceeded } from './staff-batch-limit-exceeded-notice';
import { fileTooLargeMessage, resolveStaffValidationError } from './staff-upload-error.util';

const GENERIC_VALIDATION_ERROR =
  'No se pudo validar el archivo. Verifica que sea un .xlsx de la plantilla de alta e inténtalo de nuevo.';

@Injectable()
export class StaffAltaStore {
  private readonly validationRepository = inject(StaffAltaValidationRepository);
  private readonly processRepository = inject(StaffAltaProcessRepository);
  private readonly config = inject(APP_CONFIG);
  private readonly toasts = inject(ToastService);
  private readonly selection = inject(StaffTypeSelection);

  readonly type = this.selection.type;

  private readonly _step = perType<StaffAltaWizardStep>(this.type, 1);
  private readonly _selectedFile = perType<File | null>(this.type, null);
  private readonly _validation = perType<StaffAltaValidationResult | null>(this.type, null);
  private readonly _processResult = perType<StaffAltaProcessResult | null>(this.type, null);
  private readonly _isValidating = perType(this.type, false);
  private readonly _isProcessing = perType(this.type, false);
  private readonly _validationError = perType<string | null>(this.type, null);
  private readonly _processError = perType<unknown>(this.type, null);

  readonly step = this._step.current;
  readonly validation = this._validation.current;
  readonly processResult = this._processResult.current;
  readonly isValidating = this._isValidating.current;
  readonly isProcessing = this._isProcessing.current;
  readonly validationError = this._validationError.current;
  readonly processError = this._processError.current;

  readonly selectedFileName = computed(() => this._selectedFile.current()?.name ?? null);
  readonly canProcess = computed(
    () => this._selectedFile.current() !== null && !this.isValidating() && !this.isProcessing(),
  );

  selectType(type: StaffType): void {
    if (this.type() === type || this.isValidating() || this.isProcessing()) return;
    this.selection.select(type);
  }

  selectFile(file: File): void {
    if (file.size > this.config.staff.maxUploadBytes) {
      this._selectedFile.set(null);
      this._validationError.set(fileTooLargeMessage(this.config.staff.maxUploadBytes));
      return;
    }

    this._selectedFile.set(file);
    this._validationError.set(null);
  }

  async processFormatValidation(): Promise<void> {
    const file = this._selectedFile.current();
    if (!file || this.isValidating() || this.isProcessing()) return;

    if (file.size > this.config.staff.maxUploadBytes) {
      this._validationError.set(fileTooLargeMessage(this.config.staff.maxUploadBytes));
      return;
    }

    const type = this.type();
    this._isValidating.set(true, type);
    this._validationError.set(null, type);
    try {
      const result = await firstValueFrom(this.validationRepository.validate(file, type));
      this._validation.set(result, type);
      this._processResult.set(null, type);
      this._step.set(2, type);
    } catch (cause) {
      if (notifyStaffBatchLimitExceeded(this.toasts, cause)) return;
      this._validationError.set(
        resolveStaffValidationError(cause, {
          maxUploadBytes: this.config.staff.maxUploadBytes,
          genericMessage: GENERIC_VALIDATION_ERROR,
        }),
        type,
      );
    } finally {
      this._isValidating.set(false, type);
    }
  }

  backToUpload(): void {
    if (this.isProcessing()) return;
    this._validation.set(null);
    this._processError.set(null);
    this._step.set(1);
  }

  async processSuccessful(): Promise<boolean> {
    const file = this._selectedFile.current();
    const validation = this._validation.current();
    if (!file || !validation || validation.totalOk === 0 || this.isProcessing()) return false;

    const type = this.type();
    this._isProcessing.set(true, type);
    this._processError.set(null, type);
    try {
      const result = await firstValueFrom(this.processRepository.process(file, type));
      this._processResult.set(result, type);
      this._step.set(3, type);
      return true;
    } catch (cause) {
      this._processError.set(cause, type);
      return false;
    } finally {
      this._isProcessing.set(false, type);
    }
  }

  restart(): void {
    this._selectedFile.set(null);
    this._validation.set(null);
    this._processResult.set(null);
    this._validationError.set(null);
    this._processError.set(null);
    this._step.set(1);
  }
}
